/**
 * Optimistic completion (speed.md 2, decision 157): an action that always
 * succeeds shows its finished screen at once, from a patch laid over the
 * lesson here, and the server call runs behind it. A failed try is retried
 * quietly with the same requestId (booking-contracts.md 15), so a retry can
 * never do it twice; only when the last one fails does the lesson go back
 * and a toast say so. Offline, the action waits for the connection and the
 * lesson says "Waiting for connection" meanwhile.
 *
 * The patch gives way once the server's lesson carries it (every field the
 * same), or when the action fails.
 */
import type { LiveCallBooking } from '../types/liveCall';

/** The finished screen's fields, plus what only the optimistic screen says. */
export type BookingPatch = Partial<LiveCallBooking> & {
  /** "Moved to Wed, Nov 4 at 3:00 PM" in the state card (speed.md 2). */
  movedTo?: string;
  /** Queued while offline (speed.md 2.4). */
  waiting?: boolean;
};

interface Entry { patch: BookingPatch; token: number }

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
let version = 0;
let nextToken = 1;

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}

export function subscribeOptimistic(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function optimisticVersion(): number {
  return version;
}

/** Lays a patch over a lesson; returns the token that clears it. */
export function patchBooking(bookingId: string, patch: BookingPatch): number {
  const token = nextToken++;
  const had = entries.get(bookingId)?.patch ?? {};
  entries.set(bookingId, { patch: { ...had, ...patch }, token });
  emit();
  return token;
}

/** Takes a patch off, unless a later action has laid its own since. */
export function clearPatch(bookingId: string, token?: number): void {
  const e = entries.get(bookingId);
  if (!e || (token !== undefined && e.token !== token)) return;
  entries.delete(bookingId);
  emit();
}

export function updatePatch(bookingId: string, token: number, patch: BookingPatch): void {
  const e = entries.get(bookingId);
  if (!e || e.token !== token) return;
  e.patch = { ...e.patch, ...patch };
  emit();
}

export function patchOf(bookingId: string): BookingPatch | undefined {
  return entries.get(bookingId)?.patch;
}

const OWN = new Set(['movedTo', 'waiting']);

/** Whether the server's lesson already says everything the patch says. */
export function landed(b: LiveCallBooking, patch: BookingPatch): boolean {
  return Object.entries(patch).every(([k, v]) => OWN.has(k) || JSON.stringify((b as unknown as Record<string, unknown>)[k]) === JSON.stringify(v));
}

/** The lesson as the finished screen draws it. */
export function withPatch<T extends LiveCallBooking | null | undefined>(b: T): T {
  if (!b) return b;
  const e = entries.get(b.id);
  if (!e) return b;
  return { ...b, ...e.patch } as T;
}

/** A requestId for one tap: 16 to 100 of A-Z a-z 0-9 _ - (booking-contracts.md 15). */
export function newRequestId(): string {
  const r = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '')
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `t_${r}`.slice(0, 40);
}

/** The quiet retry's waits after the first try: 1 s, 3 s, 8 s (speed.md 2.2). */
export const RETRY_WAITS_MS = [1_000, 3_000, 8_000];

/** A refusal the server meant: no retry changes it, so the screen corrects at once. */
export function isRefusal(err: unknown): boolean {
  const code = String((err as { code?: string })?.code ?? '').replace(/^functions\//, '');
  return ['invalid-argument', 'failed-precondition', 'permission-denied', 'not-found', 'already-exists',
    'out-of-range', 'unauthenticated', 'resource-exhausted', 'cancelled'].includes(code);
}

type Sleep = (ms: number) => Promise<void>;
const sleep: Sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function online(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function whenOnline(): Promise<void> {
  if (online()) return Promise.resolve();
  return new Promise((resolve) => window.addEventListener('online', () => resolve(), { once: true }));
}

/**
 * Runs a call behind the finished screen: the first try, then up to three
 * more after 1, 3 and 8 seconds, every one with the same requestId. A
 * refusal stops at once. Offline, it waits for the connection first and
 * says so through onWaiting.
 */
export async function runBehind<R>(call: (requestId: string) => Promise<R>, opts: {
  onWaiting?: (waiting: boolean) => void;
  waits?: number[];
  sleepFn?: Sleep;
  requestId?: string;
} = {}): Promise<R> {
  const requestId = opts.requestId ?? newRequestId();
  const waits = opts.waits ?? RETRY_WAITS_MS;
  const nap = opts.sleepFn ?? sleep;
  let last: unknown;
  for (let i = 0; i <= waits.length; i += 1) {
    if (i > 0) await nap(waits[i - 1]);
    if (!online()) {
      opts.onWaiting?.(true);
      await whenOnline();
      opts.onWaiting?.(false);
    }
    try {
      return await call(requestId);
    } catch (err) {
      last = err;
      if (isRefusal(err)) break;
    }
  }
  throw last;
}

/**
 * One optimistic action: patch the lesson, run the call behind, clear the
 * patch once the server's lesson carries it (the caller's subscription
 * does that through settle), or put it back and report the failure.
 */
export function completeOptimistically<R>(bookingId: string, patch: BookingPatch, call: (requestId: string) => Promise<R>, handlers: {
  onFail: (err: unknown) => void;
  onDone?: (r: R) => void;
  /** Keep the patch's own lines (movedTo) after the server lands, until the page is left. */
  keepLines?: boolean;
}): number {
  const token = patchBooking(bookingId, patch);
  void runBehind(call, { onWaiting: (w) => updatePatch(bookingId, token, { waiting: w }) })
    .then((r) => {
      handlers.onDone?.(r);
      const e = entries.get(bookingId);
      if (!e || e.token !== token) return;
      if (handlers.keepLines && e.patch.movedTo) {
        // The server is the record now: only the line stays.
        e.patch = { movedTo: e.patch.movedTo };
        emit();
      } else {
        clearPatch(bookingId, token);
      }
    })
    .catch((err) => {
      clearPatch(bookingId, token);
      handlers.onFail(err);
    });
  return token;
}

/** For tests. */
export function resetOptimistic(): void {
  entries.clear();
  emit();
}
